import React, { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, Save } from 'lucide-react';
import { DocumentLibrarySettings } from './DocumentLibrarySettings';
import { AuditSettings } from './AuditSettings';
import type { DocumentLibrarySettings as DocumentLibrarySettingsType } from './MenuSettingsTypes';
import { defaultMenuSettings } from './MenuSettingsTypes';
import { useTenancy } from '@/contexts/TenancyContext';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { toast } from 'sonner';
import { getCompanyInfo } from '@/services/companyService';
import menusService, { getMenuSettings } from '@/services/menusService';

// Audit settings interface
interface AuditSettingsType {
  findingTypes: string[];
  riskCategories: string[];
}

export default function MenuSettingsSection() {
  const { activeTenancy } = useTenancy();
  const [documentLibrarySettings, setDocumentLibrarySettings] = useState<DocumentLibrarySettingsType>(() => ({
    ...defaultMenuSettings.documentLibrary,
  }));
  
  const [auditSettings, setAuditSettings] = useState<AuditSettingsType>({
    findingTypes: [],
    riskCategories: []
  });

  // DED-605: Shared business units from GeneralSection (customerOrg.teams.businessUnitNames)
  const [sharedBusinessUnits, setSharedBusinessUnits] = useState<string[]>([]);

  const handleDocumentLibraryChange = (documentLibrary: DocumentLibrarySettingsType) => {
    setDocumentLibrarySettings(documentLibrary);
  };

  const handleAuditChange = (audit: AuditSettingsType) => {
    setAuditSettings(audit);
  };

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (activeTenancy?.id) {
      loadSettings();
    }
  }, [activeTenancy?.id]);

  const loadSettings = useCallback(async () => {
    if (!activeTenancy?.id) return;

    try {
      setIsLoading(true);
      
      // DED-605: Fetch business units from company info (same source as GeneralSection)
      const companyInfo = await getCompanyInfo(activeTenancy.id);
      if (companyInfo?.customerOrg?.teams?.businessUnitNames) {
        const businessUnitNames = companyInfo.customerOrg.teams.businessUnitNames as Record<string, { name: string }>;
        const businessUnitList = Object.values(businessUnitNames)
          .filter(bu => bu && bu.name)
          .map(bu => bu.name);
        setSharedBusinessUnits(businessUnitList);
      }
      
      // Load document library settings (existing pattern)
      const settingsRef = doc(db, 'customers', activeTenancy.id, 'companySettings', 'menus');
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        // Merge loaded settings with defaults to ensure backward compatibility
        setDocumentLibrarySettings({ ...defaultMenuSettings.documentLibrary, ...settingsSnap.data().documentLibrary });
      } else {
        // Initialize with default settings if no settings exist
        await setDoc(settingsRef, defaultMenuSettings);
        setDocumentLibrarySettings(defaultMenuSettings.documentLibrary);
      }

      // Load audit settings using menusService
      const auditMenuData = await getMenuSettings(activeTenancy.id, 'audit');
      setAuditSettings({
        findingTypes: auditMenuData.findingTypes || [],
        riskCategories: auditMenuData.riskCategories || []
      });
    } catch (error) {
      console.error('Error loading menu settings:', error);
      toast.error('Failed to load menu settings', {
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  }, [activeTenancy?.id]);

  const handleSave = async () => {
    if (!activeTenancy?.id) return;

    try {
      setIsSaving(true);

      // Save all menu settings using menusService
      await menusService.saveAllMenuSettings(activeTenancy.id, {
        documentLibrary: documentLibrarySettings,
        audit: auditSettings
      });

      // Reload settings to get the updated values
      await loadSettings();

      toast.success('Menu settings saved successfully');
    } catch (error) {
      console.error('Error saving menu settings:', error);
      toast.error('Failed to save menu settings', {
        description: error instanceof Error ? error.message : 'An unknown error occurred',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Menu Settings</h2>
          <p className="text-muted-foreground">
            Configure options for various menus throughout the application
          </p>
        </div>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <DocumentLibrarySettings
        settings={documentLibrarySettings}
        onChange={handleDocumentLibraryChange}
        sharedBusinessUnits={sharedBusinessUnits}
      />
      
      {/* DED-1682: Added spacing between Business Units and Audit Settings sections */}
      <div className="mt-8" />
      
      <AuditSettings
        settings={auditSettings}
        onChange={handleAuditChange}
      />
    </div>
  );
};
